import type { GridPosition, MapDefinition } from './types'
import { ENEMY_CATALOG, enemyMovementClass } from './enemies'
import {
  findRoute,
  GROUND_ROUTE_IDS,
  normalizeMapRoutes,
  type RouteDefinition
} from './path'

/**
 * simple-path validation rule:
 * every PATH tile must belong to a ground route.
 * Single-lane maps behave exactly as before; maps that author extra ground
 * lanes in routes[] may paint those lanes too.
 * Shared cells across multiple ground routes are legal (M68) — two routes may
 * list the same cell. Visual-only overpass without shared cells is not modeled.
 */
export const SIMPLE_PATH_RULE = 'simple-path'

export interface MapValidationError {
  rule: string
  message: string
}

export type MapValidationResult =
  | { ok: true }
  | { ok: false; errors: MapValidationError[] }

function sameCell(a: GridPosition, b: GridPosition): boolean {
  return a.x === b.x && a.y === b.y
}

function cellKey(position: GridPosition): string {
  return `${position.x},${position.y}`
}

function manhattan(a: GridPosition, b: GridPosition): number {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y)
}

export function validateMap(map: MapDefinition): MapValidationResult {
  const errors: MapValidationError[] = []
  const routes = normalizeMapRoutes(map)

  if (map.schemaVersion !== undefined) {
    if (!Number.isInteger(map.schemaVersion) || map.schemaVersion < 1) {
      errors.push({
        rule: 'schema-version',
        message: 'schemaVersion must be an integer >= 1'
      })
    } else if (map.schemaVersion > 1) {
      errors.push({
        rule: 'schema-version',
        message: `unsupported schemaVersion ${map.schemaVersion} (supported: 1)`
      })
    }
  }

  if (!Number.isInteger(map.width) || map.width <= 0 || !Number.isInteger(map.height) || map.height <= 0) {
    errors.push({
      rule: 'dimensions',
      message: 'width and height must be integers greater than 0'
    })
  }

  if (map.rows.length !== map.height) {
    errors.push({
      rule: 'row-count',
      message: `expected ${map.height} rows, found ${map.rows.length}`
    })
  }

  map.rows.forEach((row, y) => {
    if (row.length !== map.width) {
      errors.push({
        rule: 'row-length',
        message: `row ${y} length is ${row.length}, expected ${map.width}`
      })
    }
    ;[...row].forEach((char, x) => {
      if (char !== 'G' && char !== 'P') {
        errors.push({
          rule: 'tile',
          message: `unknown tile '${char}' at ${x},${y}`
        })
      }
    })
  })

  const inBounds = (position: GridPosition): boolean =>
    map.width > 0 &&
    map.height > 0 &&
    position.x >= 0 &&
    position.y >= 0 &&
    position.x < map.width &&
    position.y < map.height

  const tileAt = (position: GridPosition): string | null => {
    if (!inBounds(position)) return null
    const row = map.rows[position.y]
    if (!row || position.x >= row.length) return null
    return row[position.x]
  }

  if (!inBounds(map.spawn)) {
    errors.push({ rule: 'spawn-bounds', message: 'spawn is outside the map' })
  } else if (tileAt(map.spawn) !== 'P') {
    errors.push({ rule: 'spawn-tile', message: 'spawn tile must be PATH' })
  }

  if (!inBounds(map.exit)) {
    errors.push({ rule: 'exit-bounds', message: 'exit is outside the map' })
  } else if (tileAt(map.exit) !== 'P') {
    errors.push({ rule: 'exit-tile', message: 'exit tile must be PATH' })
  }

  if (map.path.length === 0) {
    errors.push({ rule: 'path-empty', message: 'path must contain at least one cell' })
  } else {
    if (!sameCell(map.path[0], map.spawn)) {
      errors.push({ rule: 'path-spawn', message: 'path[0] must equal spawn' })
    }
    if (!sameCell(map.path[map.path.length - 1], map.exit)) {
      errors.push({ rule: 'path-exit', message: 'path[last] must equal exit' })
    }
  }

  const seen = new Set<string>()
  map.path.forEach((cell, index) => {
    const key = cellKey(cell)
    if (seen.has(key)) {
      errors.push({
        rule: 'path-duplicate',
        message: `duplicate path cell ${cell.x},${cell.y}`
      })
    }
    seen.add(key)

    if (!inBounds(cell)) {
      errors.push({
        rule: 'path-bounds',
        message: `path cell ${index} is outside the map`
      })
      return
    }

    const tile = tileAt(cell)
    if (tile !== 'P') {
      errors.push({
        rule: 'path-tile',
        message: `path cell ${cell.x},${cell.y} is not PATH`
      })
    }

    if (index > 0) {
      const previous = map.path[index - 1]
      if (manhattan(previous, cell) !== 1) {
        errors.push({
          rule: 'path-step',
          message: `path step ${index - 1} -> ${index} is not a single orthogonal move`
        })
      }
    }
  })

  if (map.width > 0 && map.height > 0) {
    const covered = new Set(seen)
    for (const route of routes) {
      if (route.movementClass !== 'GROUND') continue
      for (const cell of route.cells) covered.add(cellKey(cell))
    }
    for (let y = 0; y < map.rows.length; y += 1) {
      const row = map.rows[y]
      if (row.length !== map.width) continue
      for (let x = 0; x < row.length; x += 1) {
        if (row[x] === 'P' && !covered.has(cellKey({ x, y }))) {
          errors.push({
            rule: SIMPLE_PATH_RULE,
            message: `simple-path validation rule: PATH tile ${x},${y} is not on a ground route`
          })
        }
      }
    }
  }

  if (!Array.isArray(map.waves)) {
    errors.push({ rule: 'waves', message: 'waves must be an array' })
  } else if (map.waves.length < 1) {
    errors.push({ rule: 'waves', message: 'playable map requires at least one wave' })
  } else {
    const waveIds = new Set<string>()
    map.waves.forEach((wave, waveIndex) => {
      if (!wave || typeof wave.id !== 'string' || wave.id.trim() === '') {
        errors.push({
          rule: 'wave-id',
          message: `wave ${waveIndex} requires a non-empty id`
        })
      } else if (waveIds.has(wave.id)) {
        errors.push({
          rule: 'wave-id',
          message: `duplicate wave id '${wave.id}'`
        })
      } else {
        waveIds.add(wave.id)
      }

      if (!Array.isArray(wave?.groups) || wave.groups.length < 1) {
        errors.push({
          rule: 'wave-groups',
          message: `wave ${wave?.id ?? waveIndex} requires at least one group`
        })
        return
      }

      if (
        !Number.isInteger(wave.completionReward) ||
        wave.completionReward < 0
      ) {
        errors.push({
          rule: 'wave-reward',
          message: `wave ${wave.id} completionReward must be an integer >= 0`
        })
      }

      wave.groups.forEach((group, groupIndex) => {
        if (!group || typeof group.enemy !== 'string' || group.enemy.trim() === '') {
          errors.push({
            rule: 'group-enemy',
            message: `wave ${wave.id} group ${groupIndex} requires enemy id`
          })
        } else if (!ENEMY_CATALOG[group.enemy]) {
          errors.push({
            rule: 'group-enemy',
            message: `wave ${wave.id} group ${groupIndex} unknown enemy '${group.enemy}'`
          })
        }
        if (!Number.isInteger(group?.count) || (group?.count ?? 0) <= 0) {
          errors.push({
            rule: 'group-count',
            message: `wave ${wave.id} group ${groupIndex} count must be an integer > 0`
          })
        }
        if (typeof group?.interval !== 'number' || group.interval < 0 || !Number.isFinite(group.interval)) {
          errors.push({
            rule: 'group-interval',
            message: `wave ${wave.id} group ${groupIndex} interval must be >= 0`
          })
        }
        if (typeof group?.delay !== 'number' || group.delay < 0 || !Number.isFinite(group.delay)) {
          errors.push({
            rule: 'group-delay',
            message: `wave ${wave.id} group ${groupIndex} delay must be >= 0`
          })
        }
        if (group?.routeId !== undefined) {
          const route = findRoute(routes, group.routeId)
          if (!route) {
            errors.push({
              rule: 'group-route',
              message: `wave ${wave.id} group ${groupIndex} unknown routeId '${group.routeId}'`
            })
          } else {
            const enemy = ENEMY_CATALOG[group.enemy]
            if (enemy) {
              const mc = enemyMovementClass(enemy)
              // Campaign V3: AIR may follow physical GROUND paths. GROUND cannot use AIR.
              const ok =
                mc === route.movementClass ||
                (mc === 'AIR' && route.movementClass === 'GROUND')
              if (!ok) {
                errors.push({
                  rule: 'group-route',
                  message: `wave ${wave.id} group ${groupIndex} enemy '${group.enemy}' cannot use ${route.movementClass} route '${route.id}'`
                })
              }
            }
          }
        } else {
          // Omitted routeId: AIR may use a dedicated air lane OR physical ground path.
          const enemy = ENEMY_CATALOG[group.enemy]
          if (enemy && enemyMovementClass(enemy) === 'AIR') {
            const hasAir = routes.some((r) => r.movementClass === 'AIR')
            const hasGround = routes.some((r) => r.movementClass === 'GROUND')
            if (!hasAir && !hasGround) {
              errors.push({
                rule: 'group-route',
                message: `wave ${wave.id} group ${groupIndex} AIR enemy '${group.enemy}' needs a navigable route on the map`
              })
            }
          }
        }
      })
    })
  }

  const primaryAir = routes.find((route) => route.movementClass === 'AIR') ?? null
  validateAirRoute(map, primaryAir, errors)
  validateRoutes(map, routes, primaryAir, errors)

  if (errors.length > 0) return { ok: false, errors }
  return { ok: true }
}

/**
 * Generic per-route checks for lanes the legacy path/airRoute rules do not
 * already cover: extra ground lanes and any additional air lanes.
 */
function validateRoutes(
  map: MapDefinition,
  routes: RouteDefinition[],
  primaryAir: RouteDefinition | null,
  errors: MapValidationError[]
): void {
  const inBounds = (position: GridPosition): boolean =>
    map.width > 0 &&
    map.height > 0 &&
    position.x >= 0 &&
    position.y >= 0 &&
    position.x < map.width &&
    position.y < map.height

  const groundCount = routes.filter((route) => route.movementClass === 'GROUND').length
  if (groundCount > GROUND_ROUTE_IDS.length) {
    errors.push({
      rule: 'route-count',
      message: `map declares ${groundCount} ground routes (max ${GROUND_ROUTE_IDS.length})`
    })
  }

  const ids = new Set<string>()
  for (const route of routes) {
    if (ids.has(route.id)) {
      errors.push({
        rule: 'route-id',
        message: `duplicate route id '${route.id}'`
      })
    }
    ids.add(route.id)

    if (route.id === primaryAir?.id) continue
    if (route.movementClass === 'GROUND' && cellsEqual(route.cells, map.path)) continue

    if (route.cells.length === 0) {
      errors.push({
        rule: 'route-empty',
        message: `route '${route.id}' must contain at least one cell`
      })
      continue
    }
    if (!sameCell(route.cells[0]!, route.spawn)) {
      errors.push({
        rule: 'route-spawn',
        message: `route '${route.id}' cells[0] must equal spawn`
      })
    }
    if (!sameCell(route.cells[route.cells.length - 1]!, route.exit)) {
      errors.push({
        rule: 'route-exit',
        message: `route '${route.id}' cells[last] must equal exit`
      })
    }

    const seen = new Set<string>()
    route.cells.forEach((cell, index) => {
      const key = cellKey(cell)
      if (seen.has(key)) {
        errors.push({
          rule: 'route-duplicate',
          message: `route '${route.id}' repeats cell ${cell.x},${cell.y}`
        })
      }
      seen.add(key)

      if (!inBounds(cell)) {
        errors.push({
          rule: 'route-bounds',
          message: `route '${route.id}' cell ${index} is outside the map`
        })
        return
      }

      if (route.movementClass === 'GROUND') {
        const row = map.rows[cell.y]
        if (row && row[cell.x] !== 'P') {
          errors.push({
            rule: 'route-tile',
            message: `route '${route.id}' cell ${cell.x},${cell.y} is not PATH`
          })
        }
      }

      if (index > 0) {
        const previous = route.cells[index - 1]!
        const step = manhattan(previous, cell)
        if (route.movementClass === 'GROUND' && step !== 1) {
          errors.push({
            rule: 'route-step',
            message: `route '${route.id}' step ${index - 1} -> ${index} is not a single orthogonal move`
          })
        } else if (route.movementClass === 'AIR' && sameCell(previous, cell)) {
          errors.push({
            rule: 'route-step',
            message: `route '${route.id}' step ${index - 1} -> ${index} has zero length`
          })
        }
      }
    })
  }
}

function cellsEqual(a: GridPosition[], b: GridPosition[] | undefined): boolean {
  if (!b || a.length !== b.length) return false
  return a.every((cell, index) => sameCell(cell, b[index]!))
}

function validateAirRoute(
  map: MapDefinition,
  air: RouteDefinition | null,
  errors: MapValidationError[]
): void {
  if (!air) return

  const inBounds = (position: GridPosition): boolean =>
    map.width > 0 &&
    map.height > 0 &&
    position.x >= 0 &&
    position.y >= 0 &&
    position.x < map.width &&
    position.y < map.height

  if (!air.cells || air.cells.length < 2) {
    errors.push({
      rule: 'air-route-empty',
      message: 'airRoute.cells must contain at least two waypoints'
    })
    return
  }

  if (!inBounds(air.spawn)) {
    errors.push({ rule: 'air-spawn-bounds', message: 'airRoute.spawn is outside the map' })
  }
  if (!inBounds(air.exit)) {
    errors.push({ rule: 'air-exit-bounds', message: 'airRoute.exit is outside the map' })
  }

  if (!sameCell(air.cells[0]!, air.spawn)) {
    errors.push({
      rule: 'air-path-spawn',
      message: 'airRoute.cells[0] must equal airRoute.spawn'
    })
  }
  if (!sameCell(air.cells[air.cells.length - 1]!, air.exit)) {
    errors.push({
      rule: 'air-path-exit',
      message: 'airRoute.cells[last] must equal airRoute.exit'
    })
  }

  const seen = new Set<string>()
  air.cells.forEach((cell, index) => {
    const key = cellKey(cell)
    if (seen.has(key)) {
      errors.push({
        rule: 'air-path-duplicate',
        message: `duplicate airRoute waypoint ${cell.x},${cell.y}`
      })
    }
    seen.add(key)

    if (!inBounds(cell)) {
      errors.push({
        rule: 'air-path-bounds',
        message: `airRoute waypoint ${index} is outside the map`
      })
      return
    }

    // AIR may jump multiple cells; disallow zero-length consecutive duplicates already handled.
    if (index > 0) {
      const previous = air.cells[index - 1]!
      if (sameCell(previous, cell)) {
        errors.push({
          rule: 'air-path-step',
          message: `airRoute step ${index - 1} -> ${index} has zero length`
        })
      }
    }
  })
}
